import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead, { injectSchema } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { featuredComics, categories } from '@/mocks/comics';

const mockChapters = (comic: typeof featuredComics[0]) => {
  const total = comic.chapters || 50;
  return Array.from({ length: Math.min(total, 20) }, (_, i) => ({
    number: total - i,
    title: comic.slug === 'secretarys-escape' && i === 0
      ? '最终的对峙'
      : comic.slug === 'demon-kings-daughter' && i === 0
      ? '龙之觉醒'
      : comic.slug === 'rebirth-of-sword-god' && i === 0
      ? '剑指苍穹'
      : `第${total - i}话`,
    updatedAt: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
    isNew: i < 3,
  }));
};

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('chapters');
  const [readingExpanded, setReadingExpanded] = useState(false);

  const comic = useMemo(() => featuredComics.find((c) => c.slug === slug), [slug]);
  const category = useMemo(() => categories.find((c) => c.slug === comic?.category), [comic]);
  const chapters = useMemo(() => (comic ? mockChapters(comic) : []), [comic]);
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return featuredComics
      .filter((c) => c.category === comic.category && c.slug !== comic.slug)
      .slice(0, 4);
  }, [comic]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  useEffect(() => {
    if (!comic) return;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 虚空韩漫网`,
      description: comic.description,
      author: { '@type': 'Person', name: comic.author },
      datePublished: '2025-01-15',
      dateModified: comic.updatedAt,
      publisher: {
        '@type': 'Organization',
        name: '虚空韩漫网',
        url: 'https://xukonghanman.com',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://xukonghanman.com/comic/${slug}`,
      },
    };
    injectSchema(schema);
  }, [comic, slug]);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到" description="抱歉，您访问的漫画页面不存在。" keywords="虚空韩漫网" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900">漫画未找到</h1>
            <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={comic.title}
        description={comic.description || `虚空韩漫网提供${comic.title}韩国漫画在线阅读，无遮挡下拉式韩漫阅读体验。${comic.author}作品，共${comic.chapters}话，${comic.status}。`}
        keywords={`虚空韩漫网,${comic.title},韩漫漫画,韩国漫画在线阅读,${category?.name || ''},${comic.author}`}
        canonicalUrl={`https://xukonghanman.com/comic/${slug}`}
        ogType="article"
        ogImage={comic.cover}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '漫画', href: category ? `/category/${category.slug}` : undefined },
                { label: comic.title },
              ]}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-[280px] flex-shrink-0">
              <div className="sticky top-24">
                <div className="rounded-lg overflow-hidden aspect-[3/4] bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 虚空韩漫网`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-4 space-y-3">
                  <Link
                    to={`/comic/${comic.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setReadingExpanded(!readingExpanded);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    {comic.status === '连载中' ? '开始阅读' : '从头阅读'}
                  </Link>
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 font-medium text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line text-lg"></i>
                    加入收藏
                  </button>
                </div>

                <div className="mt-6 bg-background-100 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">评分</span>
                    <span className="flex items-center gap-1 text-sm font-medium text-secondary-500">
                      <i className="ri-star-fill"></i>
                      {comic.rating}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">状态</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">作者</span>
                    <span className="text-xs text-foreground-800">{comic.author}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">分类</span>
                    {category && (
                      <Link
                        to={`/category/${category.slug}`}
                        className="text-xs text-primary-500 hover:text-primary-600 transition-colors duration-200"
                      >
                        {category.name}
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">总章节</span>
                    <span className="text-xs text-foreground-800">{comic.chapters}话</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                {comic.title}
              </h1>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                {comic.description}
              </p>

              <div className="flex items-center gap-2 mt-6 border-b border-background-200/70">
                <button
                  onClick={() => setActiveTab('chapters')}
                  className={`px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'chapters'
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  章节列表
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'info'
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  作品信息
                </button>
              </div>

              {activeTab === 'chapters' && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-foreground-900">
                      共 {comic.chapters} 话
                    </h3>
                    <div className="flex items-center gap-1">
                      <button className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 transition-colors duration-200 cursor-pointer">
                        <i className="ri-sort-asc"></i>
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 transition-colors duration-200 cursor-pointer">
                        <i className="ri-sort-desc"></i>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {chapters.map((ch) => (
                      <Link
                        key={ch.number}
                        to={`/comic/${comic.slug}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs text-foreground-400 font-label w-16 flex-shrink-0">
                            第{ch.number}话
                          </span>
                          <span className="text-sm text-foreground-800 truncate group-hover:text-primary-500 transition-colors duration-200">
                            {ch.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {ch.isNew && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-label bg-accent-500 text-white">NEW</span>
                          )}
                          <span className="text-xs text-foreground-400">{ch.updatedAt}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                  {comic.chapters > 20 && (
                    <button className="mt-4 w-full py-2.5 rounded-lg bg-background-100 hover:bg-background-200 text-sm text-foreground-600 transition-colors duration-200 cursor-pointer">
                      查看更多章节 (+{comic.chapters - 20})
                    </button>
                  )}
                </div>
              )}

              {activeTab === 'info' && (
                <div className="mt-6 space-y-6">
                  <div className="bg-background-100 rounded-lg p-5">
                    <h3 className="text-sm font-medium text-foreground-900 mb-3">作品简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-background-100 rounded-lg p-4">
                      <span className="text-xs text-foreground-500">作者</span>
                      <p className="text-sm text-foreground-900 mt-1 font-medium">{comic.author}</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <span className="text-xs text-foreground-500">分类</span>
                      <p className="text-sm text-foreground-900 mt-1 font-medium">{category?.name}</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <span className="text-xs text-foreground-500">总章节</span>
                      <p className="text-sm text-foreground-900 mt-1 font-medium">{comic.chapters}话</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <span className="text-xs text-foreground-500">状态</span>
                      <p className="text-sm text-foreground-900 mt-1 font-medium">{comic.status}</p>
                    </div>
                  </div>
                </div>
              )}

              {readingExpanded && (
                <div className="mt-8 border-t border-background-200/70 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-foreground-900">阅读区域</h3>
                    <button
                      onClick={() => setReadingExpanded(false)}
                      className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer"
                    >
                      收起 <i className="ri-arrow-up-s-line"></i>
                    </button>
                  </div>
                  <div className="bg-background-100 rounded-lg p-8 text-center">
                    <i className="ri-book-open-line text-4xl text-foreground-300"></i>
                    <p className="mt-3 text-sm text-foreground-500">
                      无遮挡下拉式韩漫阅读区域
                    </p>
                    <p className="mt-1 text-xs text-foreground-400">
                      在这里您将体验到流畅的滚动阅读体验，完整沉浸于{comic.title}的精彩故事之中。
                    </p>
                  </div>
                </div>
              )}

              {relatedComics.length > 0 && (
                <div className="mt-12 border-t border-background-200/70 pt-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                    同类推荐
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.slug}
                        to={`/comic/${rc.slug}`}
                        className="group cursor-pointer block"
                      >
                        <div className="rounded-lg overflow-hidden aspect-[3/4] bg-background-100">
                          <img
                            src={rc.cover}
                            alt={rc.title}
                            title={rc.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h4 className="mt-3 text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                          {rc.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}